import { memo, useCallback, type ComponentProps } from 'react';

import { useSaveJobHomeRecs } from 'src/hooks/useSaveJob/useSaveJobHomeRecs';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import useSolMetadataReference from 'src/modules/hooks/useSolMetadataReference';
import SolImpressionTracker from 'src/modules/solTracker/SolImpressionTracker';
import {
  useHomeFeedbackLoopsHideJobFeatureFlag,
  useIsHomepageRecsBadgingStrongApplicantEnabled,
} from 'src/store/featureFlags/hooks';

import { useCareerFeedAIAssistant } from '../CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';
import JobCard from '../SharedComponent/JobCard/JobCard';
import { getRecommendationBadges } from '../SharedComponent/JobCard/hooks/getRecommendationBadges';

import { useHiddenJobsHomeRecs } from './useHiddenJobsHomeRecs';

export const RECOMMENDED_JOB_VIEW_ORIGIN = 'recom-homepage';
export const RECOMMENDED_JOB_AI_VIEW_ORIGIN = 'recom-homepage-genai';

const convertToSavedJob = (
  jobInfo: ComponentProps<typeof JobCard>['jobInfo'],
) => ({
  id: jobInfo.id,
  title: jobInfo.title,
  advertiser: {
    name: jobInfo.advertiserName,
  },
  salary: jobInfo.salaryLabel
    ? {
        currencyLabel: jobInfo.salaryCurrencyLabel ?? null,
        label: jobInfo.salaryLabel,
      }
    : null,
  location: {
    label: jobInfo.locationLabel,
  },
  listedAt: jobInfo.listingDateLabel
    ? {
        label: jobInfo.listingDateLabel,
      }
    : null,
  products: jobInfo.srcLogo
    ? {
        branding: {
          logo: {
            url: jobInfo.srcLogo,
          },
        },
      }
    : null,
});

const JobCardWithTracking = (
  props: Omit<ComponentProps<typeof JobCard>, 'analytics'> & {
    position: number;
    isFeaturedOrDisplayKeyType: 'loggedInHomeRecs' | 'feedJobs';
  },
) => {
  const isHomepageRecsBadgingStrongApplicantEnabled =
    useIsHomepageRecsBadgingStrongApplicantEnabled();
  const isHomeFeedbackLoopsHideJobEnabled =
    useHomeFeedbackLoopsHideJobFeatureFlag();

  const { persistedCandidateChatContext } = useCareerFeedAIAssistant();

  const solMetadataReference = useSolMetadataReference({
    jobId: props.jobInfo.id,
    isFeaturedOrDisplayKeyType: props.isFeaturedOrDisplayKeyType,
  });

  const { toggleSavedJob } = useSaveJobHomeRecs({
    isSaved: props.jobInfo.isSaved,
    job: convertToSavedJob(props.jobInfo),
    solMetadataReference,
    analytics: {
      jobViewOriginQuery: persistedCandidateChatContext
        ? RECOMMENDED_JOB_AI_VIEW_ORIGIN
        : RECOMMENDED_JOB_VIEW_ORIGIN,
      jobListingPosition: props.position,
      jobViewType: 'job card',
      linkPosition: `${props.position}`,
    },
  });

  const { isJobHidden, hideJob, unhideJob } = useHiddenJobsHomeRecs(
    props.jobInfo.id,
  );

  const analyticsFacade = useAnalyticsFacade();

  const onSaveJobButtonClick = toggleSavedJob;
  const onHideJobButtonClick = useCallback(() => {
    if (isHomeFeedbackLoopsHideJobEnabled) {
      hideJob();
      analyticsFacade.jobAdSnippetHidePressed({
        jobId: props.jobInfo.id,
        solMetadata: props.jobInfo.solMetadata,
      });
    }
  }, [
    isHomeFeedbackLoopsHideJobEnabled,
    analyticsFacade,
    props.jobInfo,
    hideJob,
  ]);
  const onUnhideJobButtonClick = useCallback(() => {
    if (isHomeFeedbackLoopsHideJobEnabled) {
      unhideJob();
      analyticsFacade.jobAdSnippetUnhidePressed({
        jobId: props.jobInfo.id,
        solMetadata: props.jobInfo.solMetadata,
      });
    }
  }, [
    isHomeFeedbackLoopsHideJobEnabled,
    analyticsFacade,
    props.jobInfo,
    unhideJob,
  ]);

  const { analyticsBadges } = getRecommendationBadges(
    props.jobInfo.tags,
    isHomepageRecsBadgingStrongApplicantEnabled,
  );

  return (
    <SolImpressionTracker
      jobId={props.jobInfo.id}
      solMetadata={props.jobInfo.solMetadata}
      jobTags={analyticsBadges}
    >
      <JobCard
        {...props}
        analytics={{
          linkName: 'dashboard-recommended-job-click',
          linkSection: 'Recommended Jobs',
          viewOrigin: persistedCandidateChatContext
            ? RECOMMENDED_JOB_AI_VIEW_ORIGIN
            : RECOMMENDED_JOB_VIEW_ORIGIN,
          position: props.position,
          solHash: solMetadataReference,
        }}
        onSaveJobButtonClick={onSaveJobButtonClick}
        onUnhideJobButtonClick={onUnhideJobButtonClick}
        onHideJobButtonClick={onHideJobButtonClick}
        isHidden={isJobHidden}
        isHomeFeedbackLoopsHideJobEnabled={isHomeFeedbackLoopsHideJobEnabled}
      />
    </SolImpressionTracker>
  );
};

export const RecommendedJobCard = memo(JobCardWithTracking);
